import {
    ArrayShape,
    BooleanShape,
    CallExpressionShape,
    type Database,
    Shape,
    TemplateValueShape,
} from '@servicenow/sdk-build-core'
import type { Logger, Record as SdkRecord } from '@servicenow/sdk-build-core'
import { ACTIVITY_DEF_SYS_ID_TO_KEY, type ResolvedVariableType } from '../constants/activity-ootb-maps'
import { TRIGGER_TYPE_SYS_ID_TO_IDENTIFIER } from '../constants/playbook-trigger-types'
import { transformStandardPills, type PillTransformContext } from './playbook-pill-transformer'
import { START_RULE_TO_RUN_CALLEE } from './start-rule-resolver'
import {
    TRIGGER_INSTANCE_TABLE,
    ACTIVITY_TABLE,
    LANE_TABLE,
    TIMER_ATTRIBUTES_TABLE,
    VARIANT_TABLE,
    PLAYBOOK_DEFAULT_PROCESS_TYPE_DB,
} from '../constants/playbook-plugin-constants'

// boolean can be '0' or '1' in the database, so we need to convert it to a boolean manually.
// 'true' or 'false' are handled by value.toBoolean()
function convertToBoolean(value: Shape) {
    const valueAsString = value.toString().getValue()
    if (valueAsString === '0' || valueAsString === '1') {
        return new BooleanShape({ source: value, value: valueAsString === '1' })
    }
    return value.toBoolean()
}

// field_list values are stored as CSV in the database. In fluent we want to use FieldList(['str1', 'str2']).
function convertToFieldList(value: Shape) {
    const valueAsString = value.toString().getValue()
    const elements = valueAsString
        ? valueAsString
              .split(',')
              .map((s) => s.trim())
              .filter(Boolean)
        : []
    return new CallExpressionShape({
        source: value,
        callee: 'FieldList',
        args: [new ArrayShape({ source: value, elements })],
    })
}

function convertToListColumnArray(value: Shape) {
    const valueAsString = value.toString().getValue()
    const elements = valueAsString
        ? valueAsString
              .split(',')
              .map((s) => s.trim())
              .filter(Boolean)
        : []
    return new ArrayShape({
        source: value,
        elements,
    })
}
export function convertToTemplateValue(value: Shape, valueAsString?: string, pillContext?: PillTransformContext) {
    const source = value.getSource()
    const templateString = valueAsString === undefined ? value.toString() : Shape.from(source, valueAsString).asString()
    const templateValue = TemplateValueShape.from(source, templateString)
    const entries = templateValue.getTemplateValue().entries({ resolve: false })

    // If the value is not actually in template_value encoded form, preserve
    // the previous pill/string reconstruction behavior instead of forcing an
    // empty TemplateValue({}) wrapper.
    if (entries.length === 0 && templateString.getValue() !== '') {
        return pillContext
            ? (transformStandardPills(templateString.getValue(), source, pillContext) ?? templateString)
            : templateString
    }

    if (!pillContext) {
        return templateValue
    }

    const reconstructedValue: globalThis.Record<string, unknown> = {}
    for (const [field, fieldShape] of entries) {
        const transformed =
            fieldShape.ifString() !== undefined
                ? transformStandardPills(fieldShape.toString().getValue(), fieldShape, pillContext)
                : undefined
        reconstructedValue[field] = transformed ?? fieldShape
    }

    return new TemplateValueShape({
        source,
        value: reconstructedValue,
    })
}
export function convertValueToType(value: Shape, type: ResolvedVariableType) {
    switch (type) {
        case 'boolean':
            return convertToBoolean(value)
        case 'field_list':
            return convertToFieldList(value)
        case 'glide_list':
            return convertToListColumnArray(value)
        case 'template_value':
            return convertToTemplateValue(value)
        default:
            return value
    }
}

/**
 * Returns true if the playbook contains any feature Fluent cannot yet represent,
 * signalling that toShape should fall back to RecordPlugin.
 *
 * Checks (in order):
 *  1. schema_version < 3
 *  2. execution_type 'on_demand'
 *  3. Custom process type (non-default)
 *  4. Unrecognized trigger type
 *  5. timer_schedule set on a timer_attributes record
 *  6. Unrecognized activity definition
 *  6a. Optional activity (manual start rule, Run.Manually)
 *  7. Playbook has custom permission configuration
 *  8. Lane has custom permission configuration
 *  9. public_access enabled (true)
 * 10. Any variant records present (sys_pd_process_variant)
 * 11. evaluate_variant_children_after is set
 * 12. Any launcher field is set (launcher_title, launcher_description,
 *     launcher_show_record_form, launcher_record_form_view,
 *     launcher_template_fields, launcher_inputs)
 *
 * All detected issues are logged before returning so the caller learns all reasons
 * in a single pass rather than one at a time.
 */
export function hasUnsupportedFeatures(descendants: Database, record: SdkRecord, logger: Logger): boolean {
    const playbookId = record.getId().getValue()
    const reasons: string[] = []

    // Schema version — only version 3 and above is supported
    const schemaVersionField = record.get('schema_version')
    if (schemaVersionField.isUndefined()) {
        reasons.push(`Playbook has no schema_version, falling back to Record() api. Playbook = ${playbookId}`)
    } else {
        const schemaVersion = schemaVersionField.toNumber().getValue()
        if (schemaVersion < 3) {
            reasons.push(
                `Playbook has schema_version ${schemaVersion} (minimum supported is 3), ` +
                    `falling back to Record() api. Playbook = ${playbookId}`
            )
        }
    }

    // execution_type 'on_demand' is not yet supported
    const executionType = record.get('execution_type')?.ifString()?.getValue() ?? ''
    if (executionType === 'on_demand') {
        reasons.push(
            `Playbook has execution_type 'on_demand' (not yet supported by Fluent), ` +
                `falling back to Record() api. Playbook = ${playbookId}`
        )
    }

    // Process type — only the default (Standard playbook) is supported
    const processTypeField = record.get('process_type')?.ifString()?.getValue() ?? ''
    if (processTypeField !== '' && processTypeField !== PLAYBOOK_DEFAULT_PROCESS_TYPE_DB) {
        reasons.push(
            `Playbook has custom process type (not yet supported by Fluent), ` +
                `falling back to Record() api. Playbook = ${playbookId}`
        )
    }

    for (const trigger of descendants.query(TRIGGER_INSTANCE_TABLE)) {
        const typeGuid = trigger.get('trigger_type')?.ifString()?.getValue() ?? ''
        if (typeGuid && !TRIGGER_TYPE_SYS_ID_TO_IDENTIFIER[typeGuid]) {
            reasons.push(
                `Playbook has unsupported trigger type (sys_id: ${typeGuid}), ` +
                    `falling back to Record() api. Playbook = ${playbookId}`
            )
        }
    }

    // Timer records are only unsupported when timer_schedule has a value
    for (const timer of descendants.query(TIMER_ATTRIBUTES_TABLE)) {
        const timerSchedule = timer.get('timer_schedule')?.ifString()?.getValue() ?? ''
        if (timerSchedule !== '') {
            reasons.push(
                `Playbook has timer with timer_schedule set (not yet supported by Fluent), ` +
                    `falling back to Record() api. Playbook = ${playbookId}`
            )
            break
        }
    }

    for (const activity of descendants.query(ACTIVITY_TABLE)) {
        const defSysId = activity.get('activity_definition')?.ifString()?.getValue() ?? ''
        if (defSysId && !ACTIVITY_DEF_SYS_ID_TO_KEY[defSysId]) {
            reasons.push(
                `Playbook has unrecognized activity definition (sys_id: ${defSysId}), ` +
                    `falling back to Record() api. Playbook = ${playbookId}`
            )
        }

        // Optional activities (manual start rule) are not yet supported by Fluent. start_rule
        // takes precedence when it holds a recognized rule, otherwise fall back to
        // start_rule_name (mirrors resolveStartRuleToShape).
        const startRuleRaw = activity.get('start_rule')?.ifString()?.getValue() ?? ''
        const startRule = START_RULE_TO_RUN_CALLEE[startRuleRaw]
            ? startRuleRaw
            : (activity.get('start_rule_name')?.ifString()?.getValue() ?? '')
        if (startRule === 'manual') {
            reasons.push(
                `Playbook has an optional activity (manual start rule, not yet supported by Fluent), ` +
                    `falling back to Record() api. Playbook = ${playbookId}`
            )
        }
    }

    // Playbook permission — unsupported when non-default (non-empty, non-'{}')
    const permissionField = record.get('permission')?.ifString()?.getValue() ?? ''
    if (permissionField !== '' && permissionField !== '{}') {
        reasons.push(
            `Playbook has custom permission configuration (not yet supported by Fluent), ` +
                `falling back to Record() api. Playbook = ${playbookId}`
        )
    }

    // Lane permission — unsupported when any lane has a non-default permission
    for (const lane of descendants.query(LANE_TABLE)) {
        const lanePermission = lane.get('permission')?.ifString()?.getValue() ?? ''
        if (lanePermission !== '' && lanePermission !== '{}') {
            reasons.push(
                `Playbook lane has custom permission configuration (not yet supported by Fluent), ` +
                    `falling back to Record() api. Playbook = ${playbookId}`
            )
            break
        }
    }

    // public_access — unsupported when enabled (true); false is the default and already handled
    const publicAccessField = record.get('public_access')
    if (!publicAccessField.isUndefined()) {
        const publicAccess = convertToBoolean(publicAccessField).getValue()
        if (publicAccess) {
            reasons.push(
                `Playbook has public_access enabled (not yet supported by Fluent), ` +
                    `falling back to Record() api. Playbook = ${playbookId}`
            )
        }
    }

    // Variants — unsupported when any variant records exist
    const variantRecords = descendants.query(VARIANT_TABLE)
    if (variantRecords.length > 0) {
        reasons.push(
            `Playbook has variants (not yet supported by Fluent), ` +
                `falling back to Record() api. Playbook = ${playbookId}`
        )
    }

    // evaluate_variant_children_after — unsupported when set
    const evaluateVariantChildrenAfter = record.get('evaluate_variant_children_after')?.ifString()?.getValue() ?? ''
    if (evaluateVariantChildrenAfter !== '') {
        reasons.push(
            `Playbook has evaluate_variant_children_after set (not yet supported by Fluent), ` +
                `falling back to Record() api. Playbook = ${playbookId}`
        )
    }

    // Launcher fields — the on-demand launcher is not supported by Fluent. These fields can
    // appear on a supported record_driven playbook, so falling back to Record() avoids
    // silently dropping their values rather than ignoring them.
    const launcherStringFields = [
        'launcher_title',
        'launcher_description',
        'launcher_record_form_view',
        'launcher_template_fields',
        'launcher_inputs',
    ]
    const hasLauncherStringValue = launcherStringFields.some(
        (field) => (record.get(field)?.ifString()?.getValue() ?? '') !== ''
    )
    const showRecordFormField = record.get('launcher_show_record_form')
    const showRecordForm = !showRecordFormField.isUndefined() && convertToBoolean(showRecordFormField).getValue()
    if (hasLauncherStringValue || showRecordForm) {
        reasons.push(
            `Playbook has launcher field(s) set (not yet supported by Fluent), ` +
                `falling back to Record() api. Playbook = ${playbookId}`
        )
    }

    for (const reason of reasons) {
        logger.warn(reason)
    }
    return reasons.length > 0
}

// Caches the unsupported-features result per (database instance, parentSysId) pair.
// WeakMap keying on the Database object means cache entries are automatically freed
// when the Database is garbage collected (i.e. after each transform run completes),
// so results never leak between runs or test cases.
const unsupportedFeaturesCache = new WeakMap<object, Map<string, boolean>>()

/**
 * Returns true if the playbook identified by playbookSysId has any unsupported feature,
 * signalling that toShape should fall back to RecordPlugin.
 *
 * Called by all plugins in the playbook tree:
 *   - PlaybookDefinitionPlugin passes record.getId().getValue() and its pre-filtered descendants
 *     (the SDK-provided view that excludes deleted records, ensuring a deletion of an unsupported
 *     child — e.g. a variant — is correctly reflected as the playbook becoming supported)
 *   - LanePlugin, ActivityPlugin, PlaybookTriggerInstancePlugin pass their process_definition field
 *     (the foreign key that holds the sys_id of the parent sys_pd_process_definition record)
 *     and no descendants, relying on the cache being warm from PlaybookDefinitionPlugin running first
 *
 * The result is cached per (database instance, playbookSysId) so the descendant scan runs at
 * most once per playbook regardless of which plugin calls it first or how many children exist.
 */
export function isPlaybookUnsupported(
    record: SdkRecord,
    database: Database,
    logger?: Logger,
    descendants?: Database
): boolean {
    let playbookSysId = record.get('process_definition')?.ifString()?.getValue()

    let cacheForDb = unsupportedFeaturesCache.get(database)
    if (!cacheForDb) {
        cacheForDb = new Map()
        unsupportedFeaturesCache.set(database, cacheForDb)
    }

    if (playbookSysId) {
        if (!cacheForDb.has(playbookSysId)) {
            return true
        }
        return cacheForDb.get(playbookSysId)!
    }

    if (descendants && record && logger) {
        const result = hasUnsupportedFeatures(descendants, record, logger)
        playbookSysId = record.getId().getValue()
        cacheForDb.set(playbookSysId, result)
        return result
    }
    return true
}
